/**
 * Rare failures over time, as a strip of marks rather than as lines.
 *
 * THIS EXISTS BECAUSE SPLITTING THE AXIS WAS NOT ENOUGH.
 *
 * Moving failures onto their own panel fixed the worst of the magnitude
 * problem — they no longer share an axis with a volume series two orders of
 * magnitude above them. It did not fix the problem *within* the panel. Measured
 * over ninety days of realistic traffic: bounced peaks at 55 a day, unsubscribed
 * at 4, complained at 1, failed at 1. On one linear axis scaled to 55, a
 * complaint is 1.8% of the chart's height — still, for practical purposes,
 * invisible. And the complaint rate is the one that pauses sending at a tenth of
 * a percent.
 *
 * The deeper issue is that a line chart is the wrong encoding for this data. A
 * line asserts continuity between its points: it says the quantity passed
 * through every value in between. These are discrete, rare events — 2 complaints
 * on 2 days out of 91 — and the honest questions about them are "did this happen"
 * and "when", not "what was the trend".
 *
 * So presence is the primary encoding and magnitude is secondary. Every row gets
 * the same height whatever its scale, which is exactly what makes a single
 * complaint as visible as fifty-five bounces — and that is the point, because a
 * single complaint is closer to mattering.
 *
 * Legibility without colour is the same rule the rest of the design system
 * follows: intensity is opacity within one hue per row, and the row label and
 * total carry the identity, so nothing here depends on distinguishing two
 * signal colours that measure one greyscale level apart.
 */

import { formatRate, rate } from "./rates";
import type { AnalyticsBucket, Granularity } from "../api/types";

interface FailureStripProps {
  buckets: AnalyticsBucket[];
  granularity: Granularity;
}

interface Row {
  label: string;
  /** Which token colours the marks. */
  tone: "danger" | "warning" | "neutral";
  counts: number[];
  /** Denominator for the rate shown beside the total, when there is one. */
  sent: number;
  explanation: string;
}

export function FailureStrip({ buckets, granularity }: FailureStripProps) {
  const sent = buckets.reduce((n, b) => n + b.totals.sent, 0);

  const rows: Row[] = [
    {
      label: "Bounced",
      tone: "danger",
      counts: buckets.map((b) => b.totals.bounced),
      sent,
      explanation:
        "The address rejected the message. A hard bounce is permanent and suppresses the address; a soft one is retried.",
    },
    {
      label: "Complained",
      tone: "danger",
      counts: buckets.map((b) => b.totals.complained),
      sent,
      explanation:
        'Somebody pressed "this is spam". Providers weigh this far more heavily than a bounce, which is why the threshold is a tenth of a percent.',
    },
    {
      label: "Failed",
      tone: "warning",
      counts: buckets.map((b) => b.totals.failed),
      sent,
      explanation:
        "The provider refused the message before it reached a mailbox — a rate limit, a rejected sender, a malformed request.",
    },
    {
      label: "Unsubscribed",
      tone: "neutral",
      counts: buckets.map((b) => b.totals.unsubscribed),
      sent,
      explanation:
        "Not a failure, and here because it belongs beside them: it is the earliest signal that a list is being mailed too often.",
    },
  ];

  return (
    <div className="strip">
      {rows.map((row) => {
        const total = row.counts.reduce((n, c) => n + c, 0);
        // Scaled per row, not across rows. A shared scale would put the
        // complaint row back at 1.8% of full intensity, which is the failure
        // this component exists to avoid.
        const peak = Math.max(...row.counts, 1);

        return (
          <div className="strip__row" key={row.label}>
            <span className="strip__label" title={row.explanation}>
              {row.label}
            </span>

            <div
              className={`strip__track strip__track--${row.tone}`}
              role="img"
              aria-label={describeRow(row, total, buckets, granularity)}
            >
              {row.counts.map((count, i) => (
                <span
                  key={buckets[i]!.start}
                  className={`strip__cell${count > 0 ? " strip__cell--on" : ""}`}
                  // Never below 0.25, so one event is unmistakably a mark
                  // rather than a smudge somebody has to hunt for.
                  style={count > 0 ? { opacity: 0.25 + 0.75 * (count / peak) } : undefined}
                  title={`${bucketTitle(buckets[i]!.start, granularity)}: ${count} ${row.label.toLowerCase()}`}
                />
              ))}
            </div>

            <span className="strip__total oc-mono-sm">
              {total.toLocaleString()}
              {total > 0 && row.sent > 0 && (
                <span className="oc-text-muted"> · {formatRate(rate(total, row.sent))}</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * What a screen reader is told instead of the strip.
 *
 * The count and the number of days it fell on, because "3 complaints" and
 * "3 complaints all on one day" are different situations and only the second is
 * an incident.
 */
function describeRow(
  row: Row,
  total: number,
  buckets: AnalyticsBucket[],
  granularity: Granularity,
): string {
  if (total === 0) {
    return `${row.label}: none in this period.`;
  }

  const days = row.counts.filter((c) => c > 0).length;
  const unit = granularity === "hour" ? "hour" : "day";

  return (
    `${row.label}: ${total} across ${days} ${unit}${days === 1 ? "" : "s"} ` +
    `of ${buckets.length}, ${formatRate(rate(total, row.sent))} of everything sent.`
  );
}

function bucketTitle(iso: string, granularity: Granularity): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";

  return granularity === "hour"
    ? at.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit" })
    : at.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
